import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {SyncValidators} from '../../../sdk/validator/SyncValidators';
import {AlertService} from '../../../sdk/service/alert.service';
import {SubmitForm} from '../../../sdk/interface/form/submitForm';
import {
    Account,
    BusinessDetails,
    MessageCounters,
    NotificationRequest,
    NotificationResponse,
    WebAccountService,
    WebBusinessService
} from 'web-angular-client';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {Location} from '@angular/common';
import {Helper} from '../../../sdk/util/helper';
import {finalize} from 'rxjs';
import NotificationTypeEnum = NotificationRequest.NotificationTypeEnum;

@Component({
    selector: 'app-payment-request-send',
    templateUrl: './payment-request-send.component.html',
    styleUrls: ['./payment-request-send.component.scss'],
    providers: [EnvironmentService],
    standalone: false
})
export class PaymentRequestSendComponent extends SubmitForm implements OnInit
{
    account: Account;
    loadingSMS = false;
    loadingEmail = false;
    isQuickPay: boolean = false;
    messageCounters: MessageCounters;

    constructor(
        protected router: Router,
        private readonly route: ActivatedRoute,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        private readonly ngxLoader: NgxUiLoaderService,
        private readonly location: Location,
        protected authenticationService: AuthenticationService,
        protected businessService: WebBusinessService,
        private accountService: WebAccountService,
        protected environmentService: EnvironmentService,
        protected accountStatusUtil: AccountStatusUtil,
        private readonly activatedRoute: ActivatedRoute)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.route.params.subscribe(params =>
        {
            this.loadPayment(params['id']);
            this.isQuickPay = this.activatedRoute.snapshot.queryParams['isQuickPay'] === 'true';
        });
    }

    get instanceName(): string
    {
        return this.environmentService.instanceName;
    }

    ngOnInit()
    {
        this.form = this.formBuilder.group({
            customerMobileNumber: [
                '',
                SyncValidators.validateMobileNumber()
            ],
            customerEmail: ['', Validators.email],
            sendInvoice: ['true']
        });

        // get the default sendInvoice invoice setting
        this.businessService.getBusinessDetails().subscribe(
            (businessDetails: BusinessDetails) =>
            {

                if (businessDetails.sendInvoice !== undefined && businessDetails.sendInvoice != null)
                {
                    this.f.sendInvoice.setValue(businessDetails.sendInvoice);
                    this.f.sendInvoice.markAsPristine();
                }
            }
        );

        this.loadMessageCounters();
        setInterval(() =>
        {
            this.loadMessageCounters();
        }, 10000);
    }

    submit(action): void
    {
        const actionMethods = {
            sms: () => this.sendSMS(),
            email: () => this.sendEmail(),
            whatsappRtp: () => this.sendWhatsapp()
        };

        if ((actionMethods)[action])
        {
            actionMethods[action]();
        }
    }

    edit()
    {
        this.router.navigate([this.routes.SECURE.ACCOUNT.EDIT.pathSansParameters, this.account.id]).then();
    }

    back()
    {
        this.location.back();
    }

    getPayAtNumber(qrUrl: string): string
    {
        return qrUrl.substring(qrUrl.lastIndexOf('/') + 1);
    }

    public getAccountStatusUtil(): AccountStatusUtil
    {
        return this.accountStatusUtil;
    }

    public sendWhatsapp(): void
    {
        // we first open a blank tab so it does not count as an indirect use interaction.
        const whatsappTab = window.open('', '_blank');
        this.loadingSMS = true;
        this.ngxLoader.start();
        this.accountService.getWhatsappPaymentMessageContent(this.account.id)
            .pipe(finalize(() =>
            {
                this.ngxLoader.stop();
                this.loadingSMS = false;
            }))
            .subscribe({
                next: (notificationContent: string) =>
                {
                    this.openWhatsappUrl(notificationContent, whatsappTab);
                },
                error: (error) =>
                {
                    this.alertService.error(error);
                }
            });
    }

    /**
     * Send a request to pay transaction sms to the mobile number mapped to the customer mobile number input
     */
    protected sendSMS()
    {
        const notificationRequest: NotificationRequest = {
            mobileNumber: this.f.customerMobileNumber.value,
            notificationType: NotificationTypeEnum.SMS,
            initiatorType: 'MERCHANT'
        };

        this.loadingSMS = true;
        this.ngxLoader.start();
        this.accountService.sendPaymentRequestNotification(this.account.id, null, notificationRequest)
            .pipe(finalize(() =>
            {
                this.ngxLoader.stop();
                this.loadingSMS = false;
            }))
            .subscribe({
                next: (notificationResponse: NotificationResponse) =>
                {
                    if (this.hasFailedStatus(notificationResponse.status))
                    {
                        this.alertService.error(notificationResponse.responseMessage);
                    }
                    else
                    {
                        this.alertService.success(notificationResponse.responseMessage);
                    }
                },
                error: (error) =>
                {
                    this.alertService.error(error);
                }
            });
    }

    /**
     * Send a request to pay transaction email to the email address mapped to the customer email input
     */
    protected sendEmail()
    {
        const shouldSendInvoice = this.f.sendInvoice.dirty ? this.f.sendInvoice.value : null;
        this.debugOutput(this.f.sendInvoice.value);

        const notificationRequest: NotificationRequest = {
            email: this.f.customerEmail.value,
            notificationType: NotificationTypeEnum.EMAIL,
            initiatorType: 'MERCHANT'
        };

        this.loadingEmail = true;
        this.ngxLoader.start();
        this.accountService.sendPaymentRequestNotification(this.account.id, shouldSendInvoice, notificationRequest)
            .pipe(finalize(() =>
            {
                this.ngxLoader.stop();
                this.loadingEmail = false;
            }))
            .subscribe({
                next: (notificationResponse: NotificationResponse) =>
                {
                    if (this.hasFailedStatus(notificationResponse.status))
                    {
                        this.alertService.error(notificationResponse.responseMessage);
                    }
                    else if (notificationResponse.status == NotificationResponse.StatusEnum.QUEUED)
                    {
                        this.alertService.info(notificationResponse.responseMessage);
                    }
                    else
                    {
                        this.alertService.success(notificationResponse.responseMessage);
                    }
                },
                error: (error) =>
                {
                    this.alertService.error(error);
                }
            });
    }

    private openWhatsappUrl(text: string, newTab: Window)
    {
        const customerMobileNumber = this.f.customerMobileNumber.value;
        text = encodeURIComponent(text);

        // here we assign the URL to the new tab that we pre opened, this bypasses Safaris security restrictions as the
        // tab was technically opened before the async operation.
        newTab.location.assign(`https://wa.me/${this.getFormattedMobileNumberForWhatsapp(customerMobileNumber)}?text=${text}`);
    }

    private getFormattedMobileNumberForWhatsapp(customerMobileNumber: string)
    {
        if (!customerMobileNumber)
        {
            return '';
        }

        const cleaned = customerMobileNumber.replace(/[\s\-+]/g, '');

        if (!cleaned)
        {
            return '';
        }

        if (cleaned.startsWith('27'))
        {
            return cleaned;
        }
        if (cleaned.startsWith('0'))
        {
            return '27' + cleaned.substring(1);
        }
        return '27' + cleaned;
    }

    private hasFailedStatus(status: NotificationResponse.StatusEnum): boolean
    {
        return status === NotificationResponse.StatusEnum.INSUFFICIENT_CREDIT ||
            status === NotificationResponse.StatusEnum.FAILED_TO_QUEUE;
    }

    private loadPayment(id: number)
    {
        this.accountService.getAccountById(id).subscribe({
            next: (account) =>
            {
                this.account = account;
                this.account.amount = Helper.convertToMajorDenomination(account.amount);
                this.f.customerEmail.setValue(this.account.customerEmail);
                this.f.customerMobileNumber.setValue(this.account.customerMobileNumber);
            },
            error: error =>
            {
                this.alertService.error(error, true);
                this.router.navigate([this.routes.SECURE.ACCOUNT.LIST.path]).then();
            }
        });
    }

    private loadMessageCounters()
    {
        if (this.authenticationService.isLoggedIn())
        {
            this.businessService.getCounters()
                .subscribe((messageCountersDTO) =>
                {
                    this.messageCounters = messageCountersDTO;
                });
        }
    }
}